<template lang="pug">
TransitionShuffle.app-results(
	tag="div", 
	:enforce-width="true",
	:class="resultsClasses",
	:style="{'--grid-column-count': columnCount}"
)
	.app-results__calendar-toggle(v-if="config.allowCalendar && aboveMdBreakpoint", key="calendar-toggle")
		a(href, @click.prevent="toggleCalendarView") {{ calendarToggleText }}
	AppCalendar(v-if="resultView == 'calendar' && aboveMdBreakpoint", key="calendar", :results="results")
	table.app-results__table(v-else-if="resultView == 'list-minimal'", key="table")
		thead
			tr
				th
				th Document Name
				th Document Type
				th Date Published
		tbody()
			AppResultMinimal(
				v-for="(result, index) in results"
				:key="'r1'+result.id"
				:result="result"
				:index="index",
				:results="results")
	AppLrlResult(
		v-else-if="config.isLrlPage",
		v-for="(result, index) in results"
		:key="'r3'+result.id"
		:result="result"
		:index="index",
		:results="results"
	)
	AppResult(
		v-else
		v-for="(result, index) in results"
		:key="'r2'+result.id"
		:result="result"
		:index="index",
		:results="results")
	.app-results__none(v-if="!results.length", key="no_results", role="status", aria-live="polite", v-html="noResultsMarkup")
</template>
	
<script>
import { cloneDeep } from "lodash-es";
import { mapGetters, mapMutations } from "vuex";

import TransitionShuffle from '../../global/transitions/Shuffle.vue'
import AppResult from './Result.vue';
import AppLrlResult from './LegalResearchLibrary/Result.vue';
import AppResultMinimal from './ListMinimal/ResultMinimal.vue';
import AppCalendar from './Calendar/Calendar';
export default {
	name: "AppResults",
	props: {
		results: Array
	},
	components: {
		TransitionShuffle,
		AppResult,
		AppResultMinimal,
		AppCalendar,
		AppLrlResult,
	},
	data() {
		return {
			viewIsChanging: false
		};
	},
	computed: {
		...mapGetters(["resultView", "aboveMdBreakpoint", "reloading"]),
		columnCount() {
			if (!this.config.gridColumnCount || isNaN(parseInt(this.config.gridColumnCount))) return 3;
			else return parseInt(this.config.gridColumnCount);
		},
		resultsClasses() {
			return {
				'increase-row-gap': this.config.showResultType && (!this.config.showResultImage || this.resultView == 'list'),
				'app-results--list-view': this.resultView == 'list',
				'app-results--list-minimal': this.resultView == 'list-minimal',
				'app-results--no-images': (!this.config.showResultImage || this.resultView == 'list') && this.config.showResultType,
				'app-results--calendar': this.resultView == 'calendar',
				'app-results--view-change': this.viewIsChanging,
			}
		},
		calendarToggleText() {
			if (this.resultView == 'calendar') return 'Switch to List View';
			else return 'Switch to Calendar View';
		},
		noResultsMarkup() {
			if (!!this.config.noResultsText) return this.config.noResultsText;
			else return `<h3>Sorry, there were no results found for your search. Please try again.</h3>`;
		}
	},
	methods: {
		...mapMutations(["setStateValue", "extend"]),
		toggleCalendarView() {
			this.viewIsChanging = true;
			let newPageSize;
			if (this.resultView == 'calendar') {
				newPageSize = 12;
				this.setStateValue(['resultView', 'grid']);
			}	else {
				newPageSize = 100;
				this.setStateValue(['resultView', 'calendar']);
			}
			this.extend(extended => {
				let obj = cloneDeep(extended);
				obj.pageSizeOverride = newPageSize;
				return obj;
			});
			setTimeout(() => {
				this.viewIsChanging = false;
			}, 250);
		},
    getElTop() {
      let elem = this.$root.$el;
      let distance = 0;
			if (!!elem.closest(".semi-sticky-header")) {
				const header = document.querySelector("header.header");
				distance -= header.getBoundingClientRect().height;
			}
      do {
        distance += elem.offsetTop;
        elem = elem.offsetParent;
      } while (elem);
      distance = distance < 0 ? 0 : distance;
      return distance;
    },
    scrollToTop() {
      if (this.config.scrollOnReload) window.scrollTo({ top: this.getElTop(), behavior: "smooth" });
    },
	},
  watch: {
    reloading() {
      if (this.resultView != "calendar" || !this.aboveMdBreakpoint) this.scrollToTop();
    },
  },
	created() {},
	mounted() {},
};
</script>

<style lang="scss" scoped>
.app-results {
	// --grid-column-count property now set in template using columnCount computed property based on new data attribute: data-grid-column-count
	--grid-layout-gap: 1rem;
	--grid-item--min-width: 220px;
	
	/**
		* Calculated values.
		*/
	--total-gap-width: calc(var(--gap-count) * var(--grid-layout-gap));
	--grid-item--max-width: calc((100% - var(--total-gap-width)) / var(--grid-column-count));

	display: grid;
	overflow: hidden;
	grid-template-columns: 1fr;
	gap: var(--grid-layout-gap);
	transition: 0.2s ease;
	
	@include mq(sm) {
		--gap-count: calc(var(--grid-column-count) - 1);
		grid-template-columns: repeat(auto-fill, minmax(max(var(--grid-item--min-width), var(--grid-item--max-width)), 1fr));
	}
	&.app-results--no-images {
		padding-top: 1rem;
	}
	&.app-results--list-view,
	&.app-results--list-minimal {
		display: flex;
		flex-direction: column;
	}
	&.app-results--list-minimal {
		width: 100%;
	}
	&.increase-row-gap {
		row-gap: calc(1.75 * var(--grid-layout-gap));
	}
	&.app-results--calendar {
		display: flex;
		flex-direction: column;
	}
	&.app-results--view-change {
		filter: blur(16px);
	}

	&__none {
		grid-column: 1 / -1;
		margin-bottom: 1rem;
	}

	&__calendar-toggle {
		grid-column: 1 / -1;
		margin: 1rem 0;
		@include mq(lg) {
			margin: 0;
		}
		a {
			text-decoration: none;
			padding-bottom: 0.125rem;
			&:hover {
				text-decoration: underline;
				color: $blueOrg;
			}
		}
	}

	// FOR LIST-MINIMAL VIEW
	table.app-results__table {
		border: none;
		overflow: unset;
		width: 100%;
		thead,
		tbody {
			tr, 
			td, 
			th {
				border: none;
			}
		}
		thead th,
		thead td,
		tbody th {
			background-color: transparent;
			color: $darkGray;
			.page--ORG & {
				color: $lightBlackOrg;
			}
		}
		@include mqdn(lg_dn) {
			display: block;
			thead {
				display: none;
			}
			tbody {
				display: flex;
				flex-direction: column;
			}
		}
	}
}
</style>

<style lang="scss">
.app-results {
	&.app-results--view-change {
		.app-result {
			opacity: 0.6;
			transition: 0.2s ease;
			max-width: 340px;
		}
	}

	&__none {
		@include wysiwyg;
	}
}
</style>
